import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Users, Building, Calendar, TrendingUp, Activity, 
  BarChart3, PieChart, Clock, Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

export function AdminAnalytics({ api }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api().get(`/admin/analytics?days=${period}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [api, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center p-12 text-muted-foreground">
        Failed to load analytics data
      </div>
    );
  }

  const { overview, engagement, subscriptions, forum_size_distribution, feature_adoption, top_active_forums, event_breakdown, activity_trend } = analytics;

  // Calculate max activity for chart scaling
  const maxActivity = Math.max(...activity_trend.map(d => d.count), 1);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Usage Analytics</h2>
          <p className="text-muted-foreground">Platform usage and engagement metrics</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[180px]" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="total-users-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.total_users}</div>
            {overview.new_users > 0 && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3" />
                +{overview.new_users} this period
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="total-forums-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Forums</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.total_forums}</div>
            {overview.new_forums > 0 && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3" />
                +{overview.new_forums} this period
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="total-meetings-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.total_meetings}</div>
            {overview.new_meetings > 0 && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3" />
                +{overview.new_meetings} this period
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="engagement-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Engagement</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{engagement.total_agenda_items}</div>
            <p className="text-xs text-muted-foreground">agenda items created</p>
          </CardContent>
        </Card>
      </div>

      {/* Activity Trend Chart */}
      <Card data-testid="activity-trend-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Activity Trend
          </CardTitle>
          <CardDescription>Daily activity over the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-end gap-1">
            {activity_trend.map((day, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-1">
                <div 
                  className="w-full bg-primary/80 hover:bg-primary rounded-t transition-all"
                  style={{ 
                    height: `${Math.max((day.count / maxActivity) * 160, day.count > 0 ? 4 : 0)}px`,
                    minHeight: day.count > 0 ? '4px' : '0'
                  }}
                  title={`${day.date}: ${day.count} activities`}
                />
                {index % Math.ceil(activity_trend.length / 7) === 0 && (
                  <span className="text-[10px] text-muted-foreground rotate-45 origin-left">
                    {day.date.slice(5)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Subscription Status */}
        <Card data-testid="subscription-status-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Subscription Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>Active</span>
                </div>
                <span className="font-bold">{subscriptions.active}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span>Trial</span>
                </div>
                <span className="font-bold">{subscriptions.trial}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <span>Free</span>
                </div>
                <span className="font-bold">{subscriptions.free}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Expired</span>
                </div>
                <span className="font-bold">{subscriptions.expired}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span>Cancelling</span>
                </div>
                <span className="font-bold">{subscriptions.cancelling}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-500" />
                  <span>Cancelled</span>
                </div>
                <span className="font-bold">{subscriptions.cancelled}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Forum Size Distribution */}
        <Card data-testid="forum-size-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Forum Size Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(forum_size_distribution).map(([range, count]) => {
                const total = Object.values(forum_size_distribution).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={range} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span>{range} member{range !== '1' ? 's' : ''}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Feature Adoption */}
        <Card data-testid="feature-adoption-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Feature Adoption
            </CardTitle>
            <CardDescription>Percentage of forums using each feature</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Meetings Created</span>
                  <span className="text-sm font-bold">{feature_adoption.meetings}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${Math.min(feature_adoption.meetings, 100)}%` }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Date Proposals</span>
                  <span className="text-sm font-bold">{feature_adoption.date_proposals}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${Math.min(feature_adoption.date_proposals, 100)}%` }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Avg Agenda Items/Meeting</span>
                  <span className="text-sm font-bold">{feature_adoption.agenda_items}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Events */}
        <Card data-testid="top-events-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Top Activities
            </CardTitle>
            <CardDescription>Most frequent events in this period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {event_breakdown.slice(0, 8).map((event, index) => (
                <div key={event.event} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-4">{index + 1}.</span>
                    <Badge variant="outline" className="font-mono text-xs">
                      {event.event.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <span className="text-sm font-medium">{event.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Active Forums */}
      <Card data-testid="top-forums-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Top Active Forums
          </CardTitle>
          <CardDescription>Forums with the most activity in this period</CardDescription>
        </CardHeader>
        <CardContent>
          {top_active_forums.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No activity recorded yet</p>
          ) : (
            <div className="space-y-3">
              {top_active_forums.map((forum, index) => (
                <div key={forum.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-muted-foreground w-6">
                      #{index + 1}
                    </span>
                    <div>
                      <p className="font-medium">{forum.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {forum.members} members
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge 
                      variant="outline"
                      className={
                        forum.status === 'active' ? 'border-green-500 text-green-600' :
                        forum.status === 'trial' ? 'border-yellow-500 text-yellow-600' :
                        forum.status === 'free' ? 'border-purple-500 text-purple-600' :
                        'border-gray-500 text-gray-600'
                      }
                    >
                      {forum.status}
                    </Badge>
                    <p className="text-sm font-bold mt-1">{forum.activity} actions</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Engagement Stats */}
      <Card data-testid="engagement-stats-card">
        <CardHeader>
          <CardTitle>Engagement Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">{engagement.total_agenda_items}</p>
              <p className="text-xs text-muted-foreground">Agenda Items</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">{engagement.total_proposals}</p>
              <p className="text-xs text-muted-foreground">Date Proposals</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">{engagement.total_change_requests}</p>
              <p className="text-xs text-muted-foreground">Change Requests</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">{engagement.total_parking_items}</p>
              <p className="text-xs text-muted-foreground">Parking Lot Items</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">{engagement.total_cards_used}</p>
              <p className="text-xs text-muted-foreground">Cards Used</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
